<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="700">
        <v-form v-model="formData.valid" class="mb-3" validate-on="input"
                @submit.prevent="storeUser">
            <v-card style="position: relative;">
                <v-card-title id="heading" class="d-flex justify-space-between">
                    <h4>Add New User</h4>
                    <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
                </v-card-title>
                <v-overlay :model-value="!formReady" contained class="align-center justify-center">
                    <v-progress-circular indeterminate color="primary"/>
                </v-overlay>

                <v-container>
                    <v-row>
                        <v-col class="text-center" cols="12">
                            <UserAvatar :src="avatar" :first-name="formData['first_name']"
                                        :last-name="formData['last_name']" :email="formData['email']" size="150"/>
                            <v-btn flat icon size="small" @click="$refs.imageHandle.click()">
                                <v-icon icon="mdi-pencil"></v-icon>
                            </v-btn>
                            <v-file-input ref="imageHandle" v-model="formData['image']"
                                          accept="image/*" class="d-none" @change="avatarPreview"/>
                        </v-col>
                    </v-row>
                    <v-alert v-if="errors.length"
                             class="mb-3 bg-error"
                             closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>
                    <v-row>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['first_name']"
                                          :rules="[vRules.required]" autofocus density="compact" label="First Name"/>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['middle_name']" density="compact" label="Middle Name"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['last_name']"
                                          :rules="[vRules.required]" density="compact" label="Last Name"/>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['email']" :rules="[vRules.required, vRules.email]"
                                          density="compact" label="Email"/>
                        </v-col>

                        <template v-if="authUser.type !== 'admin'">
                            <v-col cols="12" sm="6">
                                <v-select v-model="formData['type']"
                                          :items="userTypes"
                                          :rules="[vRules.required]"
                                          density="compact"
                                          label="User Type"
                                          @update:model-value="()=>{formData['role'] = null; fetchRoles()}"/>
                            </v-col>

                            <v-col cols="12" sm="6">
                                <v-select v-model="formData['role']"
                                          :items="roles"
                                          :rules="authUser.type!=='admin'?[vRules.required]:[]"
                                          density="compact"
                                          item-value="id"
                                          label="User Role"/>
                            </v-col>
                        </template>

                        <v-col cols="12" sm="6">
                            <v-select v-model="formData['timezone']" :items="timezones"
                                      :rules="[vRules.required]" density="compact" item-title="title"
                                      item-value="value" label="Timezone">
                            </v-select>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['contact_phone']" density="compact" label="Contact Phone"/>
                        </v-col>

                        <template v-if="isStaff">
                            <v-col cols="12" sm="6">
                                <v-text-field v-model="formData['password']" :rules="[vRules.required, vRules.minLength(8)]"
                                              autocomplete="new-password" density="compact" label="Password" type="password"/>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field v-model="formData['password_confirmation']" :rules="[vRules.required]"
                                              autocomplete="new-password" density="compact" label="Confirm Password" type="password"/>
                            </v-col>
                        </template>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['mobile_phone']" density="compact" label="Mobile Phone"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['addr_1']" density="compact" label="Address 1"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['addr_2']" density="compact" label="Address 2"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['addr_district']" density="compact" label="District"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['addr_city']" density="compact" label="City"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['addr_state']" density="compact" label="State"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['addr_postalcode']" density="compact" label="Postal Code"/>
                        </v-col>

                        <v-col cols="12" sm="6">
                            <v-text-field v-model="formData['addr_country']" density="compact" label="Country"/>
                        </v-col>

                        <v-col v-if="isStaff" cols="12" sm="6">
                            <v-select v-model="formData['status']" :items="['disabled','registered','verified']"
                                      density="compact" label="Status"/>
                        </v-col>
                        <v-col v-else cols="12">
                            <p class="text-body-2 text-medium-emphasis mb-0 invite-note">They will get an email to set their password.</p>
                        </v-col>
                    </v-row>
                </v-container>

                <v-card-actions class="px-4 d-flex justify-end">
                    <v-btn size="small" type="button"
                           @click="$emit('update:modelValue',false)">
                        Close
                    </v-btn>
                    <v-btn :disabled="!formData.valid" color="primary" size="small" type="submit">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script setup>
import {ref, reactive, computed, onMounted} from "vue";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import timezones from "@/assets/js/timezones.json";
import {useSnackbarStore} from "@/stores/snackBarStore";
import UserAvatar from "@/components/UserAvatar.vue";

const formReady = ref(false);
const avatar = ref(null);
const snackbar = useSnackbarStore();
const authUser = useAuthStore().user;
// The API ignores password and status from anyone else and emails an invite.
const isStaff = !!authUser?.super || authUser?.type === 'admin';
const props = defineProps({
    modelValue: Boolean,
    roles: {
        type: Array,
        default: []
    }
});
const formData = reactive({
    valid: false,
    first_name: null,
    middle_name: null,
    last_name: null,
    timezone: useAuthStore().user?.timezone ?? null,
    password: null,
    password_confirmation: null,
    email: null,
    phone: null,
    addr_1: null,
    addr_2: null,
    addr_district: null,
    addr_city: null,
    addr_state: null,
    addr_postalcode: null,
    addr_country: null,
    contact_phone: null,
    mobile_phone: null,
    type: 'company'
});
const emits = defineEmits(['update:modelValue', 'addUser']);
const dialogSwitch = ref(props.modelValue);
const roles = ref(props.roles);
const errors = ref([]);
const userTypes = computed(() => {
    if (authUser.type === 'admin') {
        return [
            {value: 'admin', title: 'Admin'},
            {value: 'api', title: 'API-User'},
            {value: 'company', title: 'Company'}
        ]
    } else {
        // Company admins may only create company users. An api-type user is a
        // platform service identity (gated server-side in UserController::store), so
        // offering it here only produced a confusing 403.
        return [
            {value: 'company', title: 'Company'}
        ]
    }
});


onMounted(() => {
    const promises = [];
    if (!props.roles.length) {
        promises.push(fetchRoles());
    }
    Promise.all(promises).finally(() => formReady.value = true);
})
const avatarPreview = () => {
    avatar.value = URL.createObjectURL(formData['image']);
}
const fetchRoles = () => {
    return send('get', `roles`, {type: formData['type']})
        .then((response) => {
            roles.value = response.data.data;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Roles could not be loaded', 'error')
        })
}
const storeUser = () => {
    errors.value = [];
    if (formData['password']) {
        if (formData['password'].length < 8) {
            errors.value.push("Password must be at least 8 characters");
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
            return;
        }
        if (!formData['password_confirmation']) {
            errors.value.push("Please fill Confirm Password field");
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
            return;
        }
        if (formData['password'] !== formData['password_confirmation']) {
            errors.value.push("Password & Confirm Password mismatched");
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
            return;
        }
    }

    send('post', `users`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    }).then((response) => {
        snackbar.showSnackbar(response.data.message);
        if (response.data.success) {
            emits('addUser', response.data.data.user);
            emits('update:modelValue', false);
        }
    }).catch(function (errorResponse) {
        if (errorResponse.response.data.errors) {
            let validationErrors = errorResponse.response.data.errors;
            Object.keys(validationErrors).forEach((key) => {
                errors.value.push(validationErrors[key][0]);
            });
        } else if (errorResponse.response.data.message) {
            errors.value.push(errorResponse.response.data.message);
        }
        snackbar.showSnackbar('Something went wrong', 'error');
        document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
    })
}

</script>
